import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, SupportedLanguage, isRTL as checkRTL, languages } from '@/lib/i18n';
import { createPathHelper, getRouteWithoutLang } from '@/lib/routes';
import enTranslations from '@/locales/en.json';

// Type for the full translations object
type TranslationType = typeof enTranslations;

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: TranslationType;
  dir: 'ltr' | 'rtl';
  isRTL: boolean;
  path: (routeKey: string) => string;
  switchLanguage: (newLang: SupportedLanguage) => void;
  languages: typeof languages;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  const language = useMemo(() => {
    if (lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
      return lang as SupportedLanguage;
    }

    const stored = typeof window !== 'undefined' ? localStorage.getItem('bpo-contact-language') : null;
    const detected = (i18n.resolvedLanguage || i18n.language || '').split('-')[0];
    const candidate = (stored || detected || 'en') as SupportedLanguage;

    return SUPPORTED_LANGUAGES.includes(candidate) ? candidate : 'en';
  }, [lang, i18n.language, i18n.resolvedLanguage]);

  const isRTL = useMemo(() => checkRTL(language), [language]);
  const dir: 'ltr' | 'rtl' = isRTL ? 'rtl' : 'ltr';

  // Sync i18n with URL language
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // Update document attributes
  useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);

    // Add font classes for specific languages
    document.documentElement.classList.remove('font-arabic', 'font-chinese', 'font-russian', 'font-japanese');
    if (language === 'ar') {
      document.documentElement.classList.add('font-arabic');
    } else if (language === 'zh') {
      document.documentElement.classList.add('font-chinese');
    } else if (language === 'ru') {
      document.documentElement.classList.add('font-russian');
    } else if (language === 'ja') {
      document.documentElement.classList.add('font-japanese');
    }
  }, [language, dir]);

  const switchLanguage = useCallback((newLang: SupportedLanguage) => {
    localStorage.setItem('bpo-contact-language', newLang);
    i18n.changeLanguage(newLang);
    const currentPath = getRouteWithoutLang(location.pathname);
    navigate(`/${newLang}${currentPath}`);
  }, [i18n, location.pathname, navigate]);

  const setLanguage = switchLanguage;
  const path = useMemo(() => createPathHelper(language), [language]);

  // Get the full translations object for the current language
  // This maintains backward compatibility with t.hero.headline syntax
  const t = useMemo(() => {
    // i18n.getResourceBundle returns the translation object for the language
    const translations = i18n.getResourceBundle(language, 'translation');
    return (translations || enTranslations) as TranslationType;
  }, [language, i18n]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    dir,
    isRTL,
    path: path as unknown as (key: string) => string,
    switchLanguage,
    languages,
  }), [language, setLanguage, t, dir, isRTL, path, switchLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  const { i18n } = useTranslation();

  if (!context) {
    // Return safe fallback when outside provider (SSR, tests, etc.)
    const translations = i18n.getResourceBundle('en', 'translation') || enTranslations;
    return {
      language: 'en',
      setLanguage: () => {},
      t: translations as TranslationType,
      dir: 'ltr',
      isRTL: false,
      path: (key: string) => `/${key}`,
      switchLanguage: () => {},
      languages,
    };
  }
  return context;
}
